/**
 * GenerateMixForm — brief + selection options + Generate button.
 *
 * [REFACTOR-2026-06-06] Now generates N read-only segment-SELECTION proposals
 * (Mix Plans) via `generateMixPlans`. No render happens here — polish + export
 * are done later on the Render workbench. The brief controls selection only
 * (duration window, episode range, plot focus); transitions / BGM / captions are
 * NOT decided here.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Sparkles } from 'lucide-react'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import type { DramaLang, GenerateMixPlansResult, MixBriefInput } from '@/types/drama'
import { dramaT } from './i18n'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'

interface Props {
  projectId: string
  lang: DramaLang
  onComplete?: (result: GenerateMixPlansResult) => void
  variant?: 'inline' | 'dialog'
}

export function GenerateMixForm({ projectId, lang, onComplete, variant = 'inline' }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const [brief, setBrief] = useState('')
  const [durationMin, setDurationMin] = useState(180)
  const [durationMax, setDurationMax] = useState(360)
  const [primaryFrom, setPrimaryFrom] = useState(1)
  const [primaryTo, setPrimaryTo] = useState(20)
  const [count, setCount] = useState(3)

  const buildMixBrief = (): MixBriefInput => ({
    sop_mode: 'editor_long_mix',
    aspect_ratio: '9:16',
    duration_seconds: { min: durationMin, max: durationMax },
    count,
    primary_episode_range: [primaryFrom, primaryTo],
  })

  const genMutation = useMutation({
    mutationFn: () =>
      dramaApi.generateMixPlans(projectId, brief, { count, mixBrief: buildMixBrief() }),
    onSuccess: (data) => {
      toast({
        title: t('toast.plansGenerated'),
        description: t('toast.plansGeneratedDesc', data.plans.length),
      })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
      onComplete?.(data)
    },
    onError: (e: any) =>
      toast({
        title: t('toast.genFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const busy = genMutation.isPending

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="brief" className="text-xs mb-1 block">
          {t('gen.briefLabel')}
        </Label>
        <Textarea
          id="brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={3}
          disabled={busy}
          placeholder={t('gen.briefPlaceholder')}
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          {t('gen.briefHelp')}
        </p>
      </div>

      {/* Duration window + primary episode range */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t('gen.durationRange')}</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              min={180}
              max={390}
              value={durationMin}
              onChange={(e) => setDurationMin(parseInt(e.target.value || '180', 10))}
              disabled={busy}
              className="w-full px-2 py-1.5 border rounded-md bg-background text-sm disabled:opacity-50"
            />
            <span className="text-muted-foreground text-xs">{t('gen.to')}</span>
            <input
              type="number"
              min={180}
              max={390}
              value={durationMax}
              onChange={(e) => setDurationMax(parseInt(e.target.value || '360', 10))}
              disabled={busy}
              className="w-full px-2 py-1.5 border rounded-md bg-background text-sm disabled:opacity-50"
            />
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {t('gen.durationHint')}
          </p>
        </div>
        <div>
          <Label className="text-xs">{t('gen.episodeRange')}</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              min={1}
              value={primaryFrom}
              onChange={(e) => setPrimaryFrom(parseInt(e.target.value || '1', 10))}
              disabled={busy}
              className="w-full px-2 py-1.5 border rounded-md bg-background text-sm disabled:opacity-50"
            />
            <span className="text-muted-foreground text-xs">-</span>
            <input
              type="number"
              min={1}
              value={primaryTo}
              onChange={(e) => setPrimaryTo(parseInt(e.target.value || '20', 10))}
              disabled={busy}
              className="w-full px-2 py-1.5 border rounded-md bg-background text-sm disabled:opacity-50"
            />
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {t('gen.episodeRangeHint')}
          </p>
        </div>
      </div>

      <div
        className={
          variant === 'dialog'
            ? 'flex flex-wrap items-end justify-between gap-3'
            : 'flex flex-wrap items-end gap-3'
        }
      >
        <div className="w-28">
          <Label htmlFor="count" className="text-xs">
            {t('gen.count')}
          </Label>
          <select
            id="count"
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value, 10))}
            disabled={busy}
            className="w-full px-2 py-2 mt-1 border rounded-md bg-background text-sm disabled:opacity-50"
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {t('gen.countN', n)}
              </option>
            ))}
          </select>
        </div>
        <Button
          onClick={() => genMutation.mutate()}
          disabled={busy}
          className="bg-spectrum-magenta hover:bg-spectrum-magenta/90 text-white shadow-sm"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('gen.generating')}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" /> {t('gen.generate', count)}
            </>
          )}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        {t('gen.footer')}
      </p>
    </div>
  )
}
